/**
 * TenantThemeContext — fetches the active tenant's UI theme on mount and
 * applies every token as a `--vl-*` CSS variable on :root, so any element
 * styled with `var(--vl-btn-primary-from)` etc. instantly inherits the
 * per-tenant look. Also injects `theme.custom_css` verbatim into <head>.
 *
 * Resolution order:
 *   1. `X-Tenant-Slug` derived from the URL (e.g. /groupa/app → "groupa")
 *   2. Tenant default chrome while the backend theme is loading
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';
import { useLocation } from 'react-router';

const TenantThemeContext = createContext(null);

const DEFAULT_TENANT_CHROME = {
  bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  landing_bg: 'linear-gradient(90deg, #152235 0%, #17283A 34%, #1D3349 67%, #223B54 100%)',
  surface: 'rgba(21, 34, 53, 0.92)',
  surface_alt: 'rgba(29, 51, 73, 0.88)',
  header_from: '#152235',
  header_to: '#223B54',
};

const withTenantOverrides = (slug, theme) => {
  const base = theme || {};
  if (!slug) return base;
  return { ...DEFAULT_TENANT_CHROME, ...base };
};

const getSlugFromPath = () => {
  return tenantSlugFromPathOrHost();
};

// Map a flat theme dict { btn_primary_from: "#22FF88", ... } to CSS
// custom-property pairs { "--vl-btn-primary-from": "#22FF88", ... }.
// All non-string values are skipped so we don't render `[object Object]`.
const themeToCssVars = (theme) => {
  const out = {};
  if (!theme || typeof theme !== 'object') return out;
  for (const [key, val] of Object.entries(theme)) {
    if (typeof val !== 'string' || !val.length) continue;
    if (key === 'custom_css') continue;          // injected separately
    if (key === 'logo_url' || key === 'intro_logo_url' || key === 'brand_name' || key === 'tagline') continue;
    const cssKey = '--vl-' + key.replace(/_/g, '-');
    out[cssKey] = val;
  }
  return out;
};

export function TenantThemeProvider({ children }) {
  const location = useLocation();
  const initialSlug = getSlugFromPath();
  const [theme, setTheme] = useState(() => withTenantOverrides(initialSlug, {}));
  const [loading, setLoading] = useState(Boolean(initialSlug));
  const [ready, setReady] = useState(Boolean(!initialSlug));
  const [tenantSlug, setTenantSlug] = useState(initialSlug);
  const [tenantId, setTenantId] = useState(null);
  const requestVersion = useRef(0);
  const appliedVars = useRef(new Set());

  const applyTheme = useCallback((t) => {
    if (!t || typeof t !== 'object') return;
    const root = document.documentElement;
    const vars = themeToCssVars(t);
    for (const key of appliedVars.current) {
      if (!(key in vars)) root.style.removeProperty(key);
    }
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
    appliedVars.current = new Set(Object.keys(vars));
    // Inject custom CSS verbatim — replace any previous block so admins
    // can iterate without page reload.
    const styleId = 'vl-tenant-theme-custom-css';
    let styleEl = document.getElementById(styleId);
    const cssBody = (t.custom_css || '').toString().trim();
    if (cssBody) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = cssBody;
    } else if (styleEl) {
      styleEl.remove();
    }
  }, []);

  const fetchTheme = useCallback(async (slug) => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      document.documentElement.dataset.tenantSlug = slug || '';
      const res = await api.get('/api/tenant/theme', {
        headers: slug ? { 'X-Tenant-Slug': slug } : {},
      });
      if (version !== requestVersion.current) return;
      // An unresolved tenant gets the server's platform default theme. That
      // is not this tenant's identity and must never brand its onboarding.
      const resolvedId = res.data?.tenant_id || null;
      const t = withTenantOverrides(slug, slug && !resolvedId ? {} : (res.data?.theme || {}));
      setTenantId(resolvedId);
      setTheme(t);
      applyTheme(t);
    } catch (e) {
      if (version !== requestVersion.current) return;
      console.warn('[TenantTheme] fetch failed', e?.message);
      const fallback = withTenantOverrides(slug, {});
      setTenantId(null);
      setTheme(fallback);
      applyTheme(fallback);
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setReady(true);
      }
    }
  }, [applyTheme]);

  useEffect(() => {
    if (tenantSlug) setReady(false);
    fetchTheme(tenantSlug);
  }, [fetchTheme, tenantSlug]);

  // Re-fetch on URL change (single-page navigation between /groupa/app and /app)
  useEffect(() => {
    const handler = () => {
      const next = getSlugFromPath();
      if (next !== tenantSlug) {
        const nextTheme = withTenantOverrides(next, {});
        setTenantId(null);
        setTheme(nextTheme);
        applyTheme(nextTheme);
        setReady(Boolean(!next));
        setTenantSlug(next);
      }
    };
    // React Router push/replace navigation does not dispatch popstate.
    handler();
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [applyTheme, tenantSlug, location.pathname]);

  const value = useMemo(() => ({
    theme: theme || {},
    loading,
    tenantSlug,
    tenantId,
    refresh: () => fetchTheme(tenantSlug),
    /** Used by the admin live-preview pane to push a draft theme onto :root
     * without persisting. Pass null/empty obj to revert to the saved theme. */
    previewTheme: (draft) => applyTheme(draft || theme || {}),
  }), [theme, loading, tenantSlug, tenantId, fetchTheme, applyTheme]);

  if (tenantSlug && !ready) {
    const bootTheme = withTenantOverrides(tenantSlug, {});
    return (
      <TenantThemeContext.Provider value={value}>
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: bootTheme.bg || '#0B0E14' }}
          data-testid="tenant-theme-boot"
        >
          <div className="spinner" />
        </div>
      </TenantThemeContext.Provider>
    );
  }

  return (
    <TenantThemeContext.Provider value={value}>
      {children}
    </TenantThemeContext.Provider>
  );
}

export function useTenantTheme() {
  const ctx = useContext(TenantThemeContext);
  if (!ctx) {
    // Return a safe default so components in the tree can render without
    // the provider (e.g. landing page) — hooks must always be callable.
    return { theme: {}, loading: false, tenantSlug: null, refresh: () => {}, previewTheme: () => {} };
  }
  return ctx;
}

export { themeToCssVars };
